import isEmpty from 'lodash/isEmpty';
import sumBy from 'lodash/sumBy';
import toNumber from 'lodash/toNumber';
import moment from 'moment';
import React from 'react';
import Moment from 'react-moment';

import BoolFilter from '../generic/BoolFilter';
import CurrencyFormat from '../generic/CurrencyFormat';
import CurrencyRange from '../generic/CurrencyRange';
import DateRangeFilter from '../generic/DateRangeFilter';
import { FILTER_TYPES } from '../generic/Table/utils';
import { UI_DATE_FORMAT } from './constants';
import { getYesNoValue } from './create-csv';

export function simpleColumn(Header, accessor, width = undefined) {
  return { Header, accessor, width };
}

export function arrayColumn(...args) {
  return {
    ...simpleColumn(...args),
    Cell: row => row.value.join(', '),
    filterMethod(filter, row) {
      return row.categories
        .join(', ')
        .toLowerCase()
        .includes(filter.value.toLowerCase());
    },
  };
}

export function boolColumn(Header, accessor, width, yes = 'Yes', no = 'No') {
  return {
    ...simpleColumn(Header, accessor, width),
    Cell: row => (row.value ? yes : no),
    filterable: false,
  };
}

export function boolColumnWithFilter(Header, accessor, width) {
  return {
    ...simpleColumn(Header, accessor, width),
    Filter: props => <BoolFilter id={`${Header}-filter`} {...props} />,
    Cell: row => getYesNoValue(row.value),
    sortable: false,
  };
}

export function dateColumn(
  Header,
  accessor,
  width = undefined,
  filterable = true,
) {
  return {
    Header,
    accessor,
    width,
    filterable,
    Cell: row => {
      const date = row.original[accessor];
      return date ? <Moment format={UI_DATE_FORMAT}>{date}</Moment> : null;
    },
    filterMethod: (filter, row) => {
      const date = moment(row[accessor]).format(UI_DATE_FORMAT).toString();
      return date.includes(filter.value.toLowerCase());
    },
  };
}

export function dateRangeColumn(
  Header,
  accessor,
  width = undefined,
  filterable = true,
  Label = undefined,
) {
  return {
    Header,
    Label,
    accessor,
    width,
    filterable,

    Filter: props => <DateRangeFilter id={`${Header}-filter`} {...props} />,
    Cell: row => {
      const date = row.original[accessor];
      return date ? <Moment format={UI_DATE_FORMAT}>{date}</Moment> : null;
    },
    filterMethod: (filter, row) => {
      const {
        value: { startDate, endDate },
      } = filter;
      const rowDate = moment(row[accessor]);
      return rowDate.isBetween(startDate, endDate);
    },
    filterCriteria: FILTER_TYPES.DATE_RANGE,
  };
}

export function currencyRangeColumn({
  Header,
  accessor,
  id,
  width = undefined,
  filterable = true,
  sortable = true,
  hasPrefix = true,
  hasFooter = true,
}) {
  return {
    Header,
    accessor,
    id,
    width,
    Filter: filterable
      ? props => <CurrencyRange {...props} hasPrefix={hasPrefix} />
      : '',
    Cell: row => (
      <CurrencyFormat value={row.value || 0} text hasPrefix={hasPrefix} />
    ),
    filterMethod: (filter, row) => {
      const {
        id: filterId,
        value: { min, max },
      } = filter;
      return (
        (min || -Infinity) <= row[filterId] &&
        row[filterId] <= (max || Infinity)
      );
    },
    // eslint-disable-next-line react/prop-types
    Footer: hasFooter
      ? ({ data }) => (
          <CurrencyFormat
            value={sumBy(data, id) || 0}
            text
            hasPrefix={hasPrefix}
          />
        )
      : '',
    filterCriteria: FILTER_TYPES.RANGE,
    filterable,
    sortable,
  };
}

export const withoutSum = column => ({ ...column, Footer: undefined });

export function filterMultiBy(filter, values, key) {
  // text search:
  if (!filter.value.listIds) {
    return !isEmpty(
      values.filter(value =>
        value[key].toLowerCase().includes(filter.value.toLowerCase().trim()),
      ),
    );
  }

  // search by ids - mutli select filter
  return isEmpty(filter.value.listIds)
    ? // nothing is entered, all is returned:
      true
    : // return item if any of its associations matches:
      !isEmpty(
        filter.value.listIds.filter(
          listId =>
            !isEmpty(
              values.filter(value => toNumber(value[key]) === toNumber(listId)),
            ),
        ),
      );
}

export function filterSingleBy(filter, value) {
  // text search:
  if (!filter.value.listIds) {
    return value.toLowerCase().includes(filter.value.toLowerCase().trim());
  }

  return isEmpty(filter.value.listIds)
    ? // nothing is entered, all is returned:
      true
    : // return item if the association matches:
      filter.value.listIds
        .map(listId => toNumber(listId) === toNumber(value))
        .includes(true);
}

export function filterStartsWith(filter, value) {
  return value.toLowerCase().startsWith(filter.value.toLowerCase().trim());
}
